import { useCallback, useEffect, useRef, useState } from "react";
import { getToken, api, type Me, type ServerConfig } from "../lib/api.js";
import { deviceId } from "../lib/format.js";
import { Confirm, Drawer, Notice } from "../components/ui.js";
import { Menu, type MenuItem } from "../components/Menu.js";
import { savedHash, saveHash, forgetHash } from "../lib/credentials.js";
import { canMirror, isMirrorMessage, mirrorRoute, type MirrorInput } from "../lib/mirror.js";
import { RemoteSession, defaultOptions, type SessionState } from "rustdesk-protocol/session.js";
import { VideoSink, detectCodecs, type CodecName } from "rustdesk-protocol/video.js";
import { AudioPlayer, AudioCapture, isVoiceSupported } from "rustdesk-protocol/audio.js";
import {
  keyEventFrom, mouseMoveEvent, mouseButtonEvent, wheelEvent, toRemoteCoords,
  ctrlAltDelEvent, lockScreenEvent, peerPlatformOf, type PeerPlatform,
  mouseButtonEventFor,
  wheelEventFor,
  keyEventFor,
} from "rustdesk-protocol/input.js";
import { CursorRenderer } from "rustdesk-protocol/cursor.js";
import {
  ImageQuality, PreferCodec, peerCapabilities,
  type PeerInfo, type PeerCapabilities,
} from "rustdesk-protocol/message.js";

/**
 * The Windows session last used on a machine.
 *
 * Remembered so a reconnect, and the separate connections that files and the
 * camera open, land where the last one did instead of asking every time. It is
 * a preference, not a credential, so it lives in this browser.
 */
const sessionKey = (peerId: string) => `rd.winsession.${peerId}`;

function rememberedSession(peerId: string): number | undefined {
  try {
    const raw = localStorage.getItem(sessionKey(peerId));
    const sid = raw === null ? NaN : Number(raw);
    return Number.isFinite(sid) ? sid : undefined;
  } catch {
    return undefined;
  }
}

function rememberSession(peerId: string, sid: number): void {
  try {
    localStorage.setItem(sessionKey(peerId), String(sid));
  } catch {
    // A browser refusing storage just means it asks again next time.
  }
}
import FileBrowser from "./FileBrowser.js";
import TerminalView from "./TerminalView.js";
import CameraView from "./CameraView.js";

/**
 * Whether an event belongs to the machine rather than to the console.
 *
 * Keys and pastes are taken from the window, so that they keep working no
 * matter what holds focus. That means anything typed into a field of our own -
 * the chat box, a rename, the terminal - has to be left alone here, or it goes
 * to the machine instead of where it was typed.
 */
function meantForRemote(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return true;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return false;
  if (el.isContentEditable) return false;
  // The file browser, the terminal and the side panels are windows in their
  // own right.
  return !el.closest(".drawer, .session__side");
}

/** Hands a generated file to the browser's download machinery. */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  // Revoke late: Safari reads the blob after the click returns.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** A filename-safe local timestamp, so saved files sort by when they were taken. */
function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

const STATE_TEXT: Record<SessionState, string> = {
  idle: "Preparing",
  rendezvous: "Locating the machine",
  "waiting-peer": "Waiting for the machine to answer",
  relay: "Opening a relay",
  handshake: "Verifying the machine's identity",
  authenticating: "Signing in",
  connected: "Connected",
  closed: "Disconnected",
};

export default function Session({
  peerId,
  me,
  onExit,
  initialDisplay,
  secondaryWindow = false,
}: {
  peerId: string;
  me: Me;
  onExit: () => void;
  /** Which screen to show; set when this window was popped out for one. */
  initialDisplay?: number;
  /** True for a popped-out screen: one display, no panels, no sub-windows. */
  secondaryWindow?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<RemoteSession | null>(null);
  const sinkRef = useRef<VideoSink | null>(null);
  const cursorRef = useRef<CursorRenderer | null>(null);
  // The peer's platform decides which key-code table to use, so it must be
  // readable from the input handlers without re-binding them.
  const peerPlatformRef = useRef<PeerPlatform>("unknown");

  const [state, setState] = useState<SessionState>("idle");
  const [detail, setDetail] = useState("");
  const [error, setError] = useState("");
  const [peerInfo, setPeerInfo] = useState<PeerInfo | null>(null);
  const [needPassword, setNeedPassword] = useState(false);
  const [wrongPassword, setWrongPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [usingSaved, setUsingSaved] = useState(false);
  // The peer has asked for credentials, so a password can be offered even
  // though it has not rejected anything: "accept" mode never rejects.
  const [canSendPassword, setCanSendPassword] = useState(false);
  const [waitingForAccept, setWaitingForAccept] = useState(false);
  const [elevating, setElevating] = useState(false);
  const [elevated, setElevated] = useState(false);
  const [uacBlocking, setUacBlocking] = useState(false);
  const [notice, setNotice] = useState("");
  const [display, setDisplay] = useState(initialDisplay ?? 0);
  const [stats, setStats] = useState({ fps: 0, codec: "", width: 0, height: 0 });
  const [showFiles, setShowFiles] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [quality, setQuality] = useState<number>(ImageQuality.Balanced);
  const [inputEnabled, setInputEnabled] = useState(true);

  // Things that stay true until turned off, and that the bar keeps saying so.
  const [blockedInput, setBlockedInput] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  /** What this machine says it can do, from its PeerInfo. */
  const [caps, setCaps] = useState<PeerCapabilities>({
    privacyModeImpls: [], canBlankScreen: false, virtualDisplay: null, viewCamera: false,
  });
  const [recording, setRecording] = useState(false);

  // Side panel: only one at a time, beside the screen rather than over it.
  const [side, setSide] = useState<"none" | "chat" | "note">("none");
  const [chatLog, setChatLog] = useState<Array<{ mine: boolean; text: string; at: number }>>([]);
  const [chatDraft, setChatDraft] = useState("");
  const [unreadChat, setUnreadChat] = useState(0);
  const [note, setNote] = useState("");
  const [noteState, setNoteState] = useState<"idle" | "saving" | "saved">("idle");

  const [voice, setVoice] = useState<"off" | "asking" | "on">("off");
  const [dialog, setDialog] = useState<
    "none" | "type" | "oslogin" | "restart" | "stopservice">("none");
  /** True while files are being dragged over the remote screen. */
  const [dropping, setDropping] = useState(false);
  /** What is being sent to the machine by drag and drop, for the banner. */
  const [sendingFiles, setSendingFiles] = useState("");

  /** Every screen at once, rather than one at a time. */
  const [allScreens, setAllScreens] = useState(false);
  /** Screens the operator has popped out into their own window. */
  const [poppedOut, setPoppedOut] = useState<number[]>([]);

  /** Set below; held in a ref so the session callbacks can reach it. */
  const stopVoiceRef = useRef<() => void>(() => {});
  const recorderRef = useRef<MediaRecorder | null>(null);
  /** The .wall element, so a recording can mirror its layout. */
  const wallElRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  /** Stops the compositor that feeds a recording of the whole wall. */
  const recordStopRef = useRef<(() => void) | null>(null);
  const recordedRef = useRef<Blob[]>([]);
  const voiceRef = useRef<{ stop: () => void } | null>(null);
  /** Gives up on a call nobody answers, rather than holding the microphone. */
  const ringTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const speakerRef = useRef<AudioPlayer | null>(null);
  /** One canvas per display while every screen is shown at once. */
  const wallRef = useRef(new Map<number, VideoSink>());
  const wallCanvasRef = useRef(new Map<number, HTMLCanvasElement>());
  /**
   * Screens being drawn into another window.
   *
   * Each holds a canvas that window lent us and a decoder of its own. They
   * are why this session asks the machine for more than one screen even when
   * the all-screens view is off: the machine encodes them down this one
   * connection, so a screen in its own window costs no second login and the
   * machine still sees a single viewer.
   */
  const mirrorsRef = useRef(new Map<number, { sink: VideoSink; win: Window }>());
  /** Pan gestures in flight from those windows, one per screen. */
  const mirrorPanRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  /** Bumped when a wall canvas appears or goes, so input can re-bind to it. */
  const [wallEpoch, setWallEpoch] = useState(0);
  /**
   * One ref callback per screen, kept.
   *
   * An inline `ref={(el) => bind(i, el)}` is a new function on every render,
   * which React reads as a different ref: it detaches and reattaches each
   * canvas every time, tearing down that screen's decoder before it can paint.
   */
  const wallRefs = useRef(new Map<number, (el: HTMLCanvasElement | null) => void>());
  /** Which screen the pointer is over while every screen is shown at once. */
  const [activeWall, setActiveWall] = useState(0);
  /**
   * Set when the machine has several Windows sessions and is waiting to be
   * told which - a terminal server, or a desktop someone is on over RDP.
   */
  const [sessionChoice, setSessionChoice] = useState<{
    sessions: Array<{ sid: number; name: string }>;
    currentSid: number;
  } | null>(null);
  const [windowsSessions, setWindowsSessions] = useState<
    Array<{ sid: number; name: string }>>([]);
  /**
   * What the machine currently allows. Everything starts permitted; the
   * machine announces only what it takes away.
   */
  const [allowed, setAllowed] = useState<Record<string, boolean>>({});
  const [currentSid, setCurrentSid] = useState(0);
  /**
   * Text the machine copied that this browser would not take automatically.
   * Offered for the operator to copy by hand rather than being lost.
   */
  const [clipboardFromPeer, setClipboardFromPeer] = useState("");
  /** Resolutions the machine says the current screen can take. */
  const [resolutions, setResolutions] = useState<Array<{ width: number; height: number }>>([]);
  /** Follow whichever screen the person at the machine is using. */
  const [followDisplay, setFollowDisplay] = useState(false);

  // Keep the current display in a ref: the pointer handlers are attached once
  // and must not close over a stale value.
  const displayRef = useRef(initialDisplay ?? 0);
  displayRef.current = display;
  /*
   * The same trick for the three other things a callback outside React's
   * render needs to read: a message handler bound once would otherwise keep
   * reading whatever these were when it was bound.
   */
  const allScreensRef = useRef(false);
  allScreensRef.current = allScreens;
  const stateRef = useRef<SessionState>("idle");
  stateRef.current = state;
  const inputEnabledRef = useRef(true);
  inputEnabledRef.current = inputEnabled;
  const peerInfoRef = useRef<PeerInfo | null>(null);
  peerInfoRef.current = peerInfo;
  /** A password to persist once the peer accepts it. */
  const pendingSaveRef = useRef<string | null>(null);
  /** Windows credentials to present at the machine's own sign-in screen. */
  const osLoginRef = useRef<{ username: string; password: string } | null>(null);

  const start = useCallback(
    async (withPasswordArg?: string) => {
      let withPassword = withPasswordArg;
      setError("");
      setNeedPassword(false);

      const canvas = canvasRef.current;
      if (!canvas) return;

      let cfg: ServerConfig;
      try {
        cfg = await api.get<ServerConfig>("/api/console/generator/config");
      } catch (e) {
        setError((e as Error).message);
        return;
      }

      const codecs = await detectCodecs();
      const sink = new VideoSink(canvas, {
        onError: (m) => setError(m),
        onNeedRefresh: () => sessionRef.current?.refreshVideo(),
      });
      sinkRef.current = sink;
      cursorRef.current = new CursorRenderer(canvas);

      const options = {
        ...defaultOptions(),
        imageQuality: quality,
        supportedDecoding: {
          vp8: codecs.vp8,
          vp9: codecs.vp9,
          h264: codecs.h264,
          h265: codecs.h265,
          av1: codecs.av1,
          // Prefer a codec this browser has actually reported support for.
          prefer: codecs.vp9 ? PreferCodec.VP9 : codecs.h264 ? PreferCodec.H264 : PreferCodec.Auto,
        },
      };

      // A password staged from the machine list is used once, then saved
      // properly as a hash when the machine accepts it.
      let staged = "";
      try {
        staged = sessionStorage.getItem(`rd.pw.${peerId}`) ?? "";
      } catch {
        staged = "";
      }
      if (!withPassword && staged) {
        withPassword = staged;
        pendingSaveRef.current = staged;
        try { sessionStorage.removeItem(`rd.pw.${peerId}`); } catch { /* ignore */ }
      }

      // A saved hash lets this connect with no prompt at all.
      const saved = withPassword ? null : await savedHash(peerId);
      setUsingSaved(!!saved);

      const session = new RemoteSession(
        {
          origin: window.location.origin,
          token: getToken(),
          peerId,
          serverKey: cfg.key,
          password: withPassword,
          passwordHash: saved ?? undefined,
          myName: me.display_name || me.name,
          myId: "web",
          windowsSessionId: rememberedSession(peerId),
          osUsername: osLoginRef.current?.username,
          osPassword: osLoginRef.current?.password,
          options,
        },
        {
          onState: (s, d) => {
            setState(s);
            setDetail(d ?? "");
            if (s === "authenticating") {
              // The peer has sent its salt, so credentials can be offered now.
              setCanSendPassword(true);
              // With no password to send, the peer is asking its own user to
              // accept. Say so rather than showing a bare spinner.
              setWaitingForAccept(!withPassword && !saved);
            }
            if (s === "connected") {
              setWaitingForAccept(false);
              setNeedPassword(false);
            }
          },
          onPeerInfo: (info) => {
            setPeerInfo(info);
            setCaps(peerCapabilities(info));
            setResolutions(info.resolutions);
            setWindowsSessions(info.windowsSessions.sessions);
            setCurrentSid(info.windowsSessions.currentSid);
            // A popped-out window was opened for one screen and must stay on
            // it; otherwise follow whichever the machine is showing.
            if (initialDisplay === undefined) {
              // A screen can disappear - a virtual display removed, a monitor
              // unplugged - and pointing at one that is gone leaves the
              // session with no picture at all.
              const last = Math.max(0, info.displays.length - 1);
              setDisplay(Math.min(info.currentDisplay, last));
              if (displayRef.current > last) {
                displayRef.current = last;
                sessionRef.current?.switchDisplay(last);
              }
            } else if (initialDisplay !== info.currentDisplay) {
              sessionRef.current?.switchDisplay(initialDisplay);
            }
            peerPlatformRef.current = peerPlatformOf(info.platform);
            // Only store it now: the peer has accepted it.
            const pw = pendingSaveRef.current;
            if (pw) {
              pendingSaveRef.current = null;
              const salt = sessionRef.current?.salt ?? "";
              if (salt) {
                void saveHash(peerId, pw, salt, {
                  hostname: info.hostname, platform: info.platform, username: info.username,
                }).catch(() => {});
              }
            }
            // Ask for a key frame straight away rather than waiting for the
            // peer's next scheduled one.
            sessionRef.current?.refreshVideo();
          },
          onError: (m) => setError(m),
          onClose: (r) => setDetail(r),
          onChat: (text) => {
            setChatLog((log) => [...log, { mine: false, text, at: Date.now() }]);
            // Only count it as unread when the panel is not the one on screen.
            setSide((cur) => {
              if (cur !== "chat") setUnreadChat((n) => n + 1);
              return cur;
            });
          },
          onScreenshot: ({ ok, message, data }) => {
            if (!ok) {
              setError(message || "The machine could not take a screenshot.");
              return;
            }
            downloadBlob(
              new Blob([data.slice() as unknown as BlobPart], { type: "image/png" }),
              `${peerInfoRef.current?.hostname || peerId}-${stamp()}.png`,
            );
            setNotice("Screenshot saved.");
          },
          onPermissions: (permissions) => {
            setAllowed({ ...permissions });
            // Say so once, rather than leaving controls that quietly do
            // nothing: the person at the machine did this deliberately.
            const off = Object.entries(permissions)
              .filter(([, on]) => !on)
              .map(([name]) => name);
            if (off.length) {
              setNotice(`The machine has turned off: ${off.join(", ")}.`);
            }
          },
          /**
           * Text copied on the machine.
           *
           * Written to this browser's clipboard so it can be pasted locally.
           * A browser only allows that while the page has focus and with
           * permission, so a refusal is kept quiet - it is not an error worth
           * interrupting a session for.
           */
          onClipboard: (text) => {
            if (!text) return;
            void navigator.clipboard?.writeText(text).catch(() => {
              setClipboardFromPeer(text);
            });
          },
          onClipboardImage: ({ bytes }) => {
            // An image copied at the machine. A browser will only accept a
            // PNG on the clipboard, and only with permission, so a refusal
            // offers it as a download instead of losing it.
            const blob = new Blob([bytes.slice() as unknown as BlobPart], { type: "image/png" });
            const write = typeof ClipboardItem === "undefined"
              ? Promise.reject(new Error("no ClipboardItem"))
              : navigator.clipboard?.write([new ClipboardItem({ "image/png": blob })]);
            void Promise.resolve(write)
              .then(() => setNotice("An image was copied from the machine."))
              .catch(() => {
                downloadBlob(blob, `${peerInfoRef.current?.hostname || peerId}-${stamp()}.png`);
                setNotice("An image was copied at the machine; it has been saved instead.");
              });
          },
          onSwitchSidesRequest: () => {
            setError(
              "The machine asked to control this computer instead. A browser has no " +
              "screen to hand over, so that was declined.",
            );
          },
          onWindowsSessionRequired: (info) => {
            // The machine starts nothing until this is answered, so it is put
            // in front of the operator rather than logged.
            setSessionChoice(info);
          },
          onPrivacyMode: ({ on, ok, message }) => {
            // A correction, and the only place a refusal can be explained:
            // the machine can have no driver for this, or someone else may
            // already have it on.
            setPrivacy(on);
            if (ok) {
              setNotice(message || (on
                ? "The machine's own screen is blanked."
                : "The machine's own screen is back."));
            } else {
              setError(message || "The machine could not blank its screen.");
            }
          },
          onBlockInput: ({ blocked, ok, message }) => {
            // Only a correction: the toggle already moved when it was clicked.
            setBlockedInput(blocked);
            if (!ok) setError(message);
          },
          onVoiceCall: (accepted) => {
            if (ringTimerRef.current) clearTimeout(ringTimerRef.current);
            ringTimerRef.current = null;
            if (accepted) {
              setVoice("on");
              setNotice("The call is connected.");
            } else {
              setVoice("off");
              stopVoiceRef.current();
              setError("The call was declined at the machine.");
            }
          },
          onAudioFormat: (format) => speakerRef.current?.setFormat(format),
          onAudio: (data) => speakerRef.current?.push(data),
          onElevation: ({ ok, message }) => {
            setElevating(false);
            if (ok) {
              setElevated(true);
              setNotice("The machine is now running with administrator rights.");
            } else {
              setError(message || "Elevation was refused.");
            }
          },
          onElevationState: (st) => {
            // The peer reports a UAC dialog, or a focused window running
            // elevated while it is not - in both cases input is swallowed.
            if (st.uac !== undefined) setUacBlocking(st.uac);
            if (st.elevated !== undefined) setUacBlocking(st.elevated);
          },
          onPasswordRequired: (wrong) => {
            setNeedPassword(true);
            setWrongPassword(wrong);
            setWaitingForAccept(false);
            // A saved hash that no longer works must not be retried forever.
            if (wrong && saved) {
              void forgetHash(peerId).catch(() => {});
              setUsingSaved(false);
            }
          },
          onMessage: (msg) => {
            switch (msg.kind) {
              case "video_frame": {
                if (!msg.frames.length) return;
                const codec = msg.codec as CodecName;
                // While every screen is shown at once the peer sends a stream
                // per display, each with its own decoder and canvas.
                const wall = wallRef.current.get(msg.display);
                if (wall) {
                  if (!wall.ready(codec)) void wall.configure(codec);
                  for (const f of msg.frames) wall.decode(f.data, f.key, f.pts);
                  return;
                }
                // A screen in its own window: decoded here, drawn straight
                // into the canvas that window lent us.
                const mirror = mirrorsRef.current.get(msg.display);
                if (mirror) {
                  if (!mirror.sink.ready(codec)) void mirror.sink.configure(codec);
                  for (const f of msg.frames) mirror.sink.decode(f.data, f.key, f.pts);
                  // The screen on the main stage may be this one as well.
                  if (msg.display !== displayRef.current) return;
                }
                // Otherwise only the display being viewed is painted.
                if (msg.display !== displayRef.current) return;
                if (!sink.ready(codec)) {
                  // configure() queues the frames it needs; handing them over
                  // here as well would decode them twice.
                  void sink.configure(codec);
                  for (const f of msg.frames) sink.decode(f.data, f.key, f.pts);
                  return;
                }
                for (const f of msg.frames) sink.decode(f.data, f.key, f.pts);
                return;
              }
              case "cursor_data":
                void cursorRef.current?.update(msg);
                return;
              case "cursor_id":
                cursorRef.current?.use(msg.id);
                return;
              default:
                return;
            }
          },
        },
      );
      sessionRef.current = session;

      try {
        await session.connect();
      } catch {
        /* the error surfaced through onError already */
      }
    },
    [peerId, me, quality],
  );

  useEffect(() => {
    void start();
    return () => {
      // Anything still running has to be stopped here: a recording holds a
      // stream, a call holds the microphone, and each extra screen holds a
      // decoder. None of them would be released by closing the session alone.
      try { recorderRef.current?.stop(); } catch { /* already stopped */ }
      recordStopRef.current?.();
      stopVoiceRef.current();
      for (const wall of wallRef.current.values()) wall.close();
      wallRef.current.clear();
      wallCanvasRef.current.clear();
      sessionRef.current?.close("closed by user");
      sinkRef.current?.close();
      cursorRef.current?.reset();
      sessionRef.current = null;
      sinkRef.current = null;
      cursorRef.current = null;
    };
    // Intentionally only on mount: reconnects go through the button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The source is fixed when a recording starts, so changing the view would
  // record a black rectangle from then on. Save what there is instead.
  useEffect(() => {
    if (!recording) return;
    recorderRef.current?.stop();
    setNotice("The view changed, so the recording was saved.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allScreens]);

  // A recording or a call left running would outlive the tab silently.
  useEffect(() => {
    if (!recording && voice === "off") return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [recording, voice]);

  // The note lives on the device record, so it is there for anyone else too.
  useEffect(() => {
    if (side !== "note") return;
    let alive = true;
    void api
      .get<{ data: Array<{ id: string; note: string }> }>("/api/console/devices?pageSize=500")
      .then((page) => {
        if (!alive) return;
        const device = page.data.find((d) => d.id === peerId);
        if (device) setNote(device.note ?? "");
      })
      .catch(() => { /* an unreachable console must not break the session */ });
    return () => { alive = false; };
  }, [side, peerId]);

  // Opening the chat clears its unread count.
  useEffect(() => {
    if (side === "chat") setUnreadChat(0);
  }, [side]);

  // Sample decoder stats on a timer rather than per frame, so painting is not
  // throttled by React re-renders.
  useEffect(() => {
    const t = setInterval(() => {
      if (allScreens && wallRef.current.size) {
        // Every panel has its own decoder; report the whole wall, or the bar
        // sits frozen on the last single-screen reading.
        let fps = 0;
        let codec = "";
        for (const sink of wallRef.current.values()) {
          const w = sink.stats;
          fps += w.fps;
          codec ||= w.codec ?? "";
        }
        setStats({ fps, codec, width: 0, height: 0 });
        return;
      }
      const s = sinkRef.current?.stats;
      if (s) {
        setStats({ fps: s.fps, codec: s.codec ?? "", width: s.width, height: s.height });
        // Tell the machine what this browser is keeping up with, so it caps
        // what it encodes to that instead of guessing from network delay.
        if (s.fps > 0) sessionRef.current?.reportDecodeRate(s.fps);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [allScreens]);

  /**
   * Where a screen sits on the machine's desktop.
   *
   * The peer wants a position on the whole desktop, not within one screen, so
   * the screen's own origin is part of it - which is what lets a click on any
   * panel of the wall land on the right screen.
   */
  const viewportFor = useCallback((index: number) => {
    const d = peerInfoRef.current?.displays[index];
    const canvas = wallCanvasRef.current.get(index) ?? canvasRef.current;
    return {
      // Fall back to the decoded frame size before PeerInfo names the display.
      remoteWidth: d?.width || canvas?.width || 0,
      remoteHeight: d?.height || canvas?.height || 0,
      remoteX: d?.x ?? 0,
      remoteY: d?.y ?? 0,
    };
  }, []);

  // --- input wiring ------------------------------------------------------
  //
  // Bound to whichever screens are on show: the single canvas normally, or
  // every panel of the wall. Binding only the main canvas left the wall
  // view-only, because it is hidden while the wall is up and a hidden element
  // receives no events.
  useEffect(() => {
    const targets: Array<{ el: HTMLCanvasElement; display: number }> = allScreens
      ? [...wallCanvasRef.current].map(([display, el]) => ({ el, display }))
      : canvasRef.current
        ? [{ el: canvasRef.current, display: displayRef.current }]
        : [];

    // The machine's pointer is drawn as a CSS cursor, so it has to be put on
    // every panel on show - otherwise the wall has neither the remote pointer
    // nor the browser's own, and the cursor simply vanishes.
    cursorRef.current?.setTargets(targets.map((t) => t.el));
    cursorRef.current?.setVisible(inputEnabled);
    if (!inputEnabled) return;
    if (!targets.length) return;

    const coordsIn = (t: { el: HTMLCanvasElement; display: number }, e: MouseEvent) =>
      toRemoteCoords(e.clientX, e.clientY, t.el.getBoundingClientRect(), viewportFor(t.display));

    /** The panel the pointer was last over, for a button released off-canvas. */
    let last = targets[0]!;

    const cleanups: Array<() => void> = [];
    for (const t of targets) {
      const onMove = (e: MouseEvent) => {
        last = t;
        if (allScreens) setActiveWall(t.display);
        sessionRef.current?.sendMouse(mouseMoveEvent(coordsIn(t, e)));
      };
      const onDown = (e: MouseEvent) => {
        e.preventDefault();
        last = t;
        const ev = mouseButtonEvent(e, true, coordsIn(t, e));
        if (ev) sessionRef.current?.sendMouse(ev);
      };
      let panning = false;
      let panTimer: ReturnType<typeof setTimeout> | null = null;
      const onWheel = (e: WheelEvent) => {
        e.preventDefault();
        // A trackpad reports pixel deltas and a mouse wheel reports lines.
        // Sending a pan for the former scrolls by exactly the amount asked
        // for, the way it feels locally, instead of in whole notches.
        if (e.deltaMode === 0 && !e.ctrlKey) {
          const at = coordsIn(t, e);
          if (!panning) {
            panning = true;
            sessionRef.current?.sendTouchPan("start", at.x, at.y);
          }
          sessionRef.current?.sendTouchPan("update", -e.deltaX, -e.deltaY);
          if (panTimer) clearTimeout(panTimer);
          panTimer = setTimeout(() => {
            panning = false;
            sessionRef.current?.sendTouchPan("end", at.x, at.y);
          }, 120);
          return;
        }
        // Ctrl with a wheel is a pinch on every trackpad.
        if (e.ctrlKey && e.deltaMode === 0) {
          sessionRef.current?.sendTouchScale(-e.deltaY / 100);
          return;
        }
        sessionRef.current?.sendMouse(wheelEvent(e));
      };
      const onContext = (e: Event) => e.preventDefault();

      t.el.addEventListener("mousemove", onMove);
      t.el.addEventListener("mousedown", onDown);
      t.el.addEventListener("wheel", onWheel, { passive: false });
      t.el.addEventListener("contextmenu", onContext);
      cleanups.push(() => {
        if (panTimer) clearTimeout(panTimer);
        t.el.removeEventListener("mousemove", onMove);
        t.el.removeEventListener("mousedown", onDown);
        t.el.removeEventListener("wheel", onWheel);
        t.el.removeEventListener("contextmenu", onContext);
      });
    }

    // On the window, so a button released outside a panel still lifts on the
    // machine; otherwise it stays down.
    const onUp = (e: MouseEvent) => {
      const ev = mouseButtonEvent(e, false, coordsIn(last, e));
      if (ev) sessionRef.current?.sendMouse(ev);
    };
    window.addEventListener("mouseup", onUp);
    cleanups.push(() => window.removeEventListener("mouseup", onUp));

    /*
     * Keys are taken from the window rather than the canvas. A canvas only
     * receives them while it holds focus, and focus goes to the toolbar the
     * moment a menu is used - so the keyboard died after every screen switch
     * until you clicked back on the picture.
     */
    const onKeyDown = (e: KeyboardEvent) => {
      if (!meantForRemote(e.target)) return;
      e.preventDefault();
      const ev = keyEventFrom(e, true, peerPlatformRef.current);
      if (ev) sessionRef.current?.sendKey(ev);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!meantForRemote(e.target)) return;
      e.preventDefault();
      const ev = keyEventFrom(e, false, peerPlatformRef.current);
      if (ev) sessionRef.current?.sendKey(ev);
    };
    // A key held while the page loses focus would never see its key-up and
    // would stay down on the machine. Release the modifiers on the way out.
    const onBlur = () => {
      for (const code of ["ShiftLeft", "ShiftRight", "ControlLeft", "ControlRight",
                          "AltLeft", "AltRight", "MetaLeft", "MetaRight"]) {
        const ev = keyEventFrom(
          { code, key: "", ctrlKey: false, shiftKey: false, altKey: false, metaKey: false } as KeyboardEvent,
          false,
          peerPlatformRef.current,
        );
        if (ev) sessionRef.current?.sendKey(ev);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    cleanups.push(() => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    });

    return () => { for (const off of cleanups) off(); };
  }, [inputEnabled, allScreens, wallEpoch, viewportFor]);

  /**
   * Files dropped on the remote screen go to the machine's desktop.
   *
   * What people reach for when they say "copy a file across": the clipboard
   * cannot carry one between a browser and a remote Windows shell, and a drop
   * is the same gesture without the protocol.
   */
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const carriesFiles = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes("Files");

    const onOver = (e: DragEvent) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      setDropping(true);
    };
    const onLeave = (e: DragEvent) => {
      if (e.relatedTarget && stage.contains(e.relatedTarget as Node)) return;
      setDropping(false);
    };
    const onDrop = async (e: DragEvent) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      setDropping(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (!files.length) return;
      if (allowed.file === false) {
        setError("File transfer is turned off at the machine.");
        return;
      }
      await sendFilesToMachine(files);
    };

    stage.addEventListener("dragover", onOver);
    stage.addEventListener("dragleave", onLeave);
    stage.addEventListener("drop", onDrop);
    return () => {
      stage.removeEventListener("dragover", onOver);
      stage.removeEventListener("dragleave", onLeave);
      stage.removeEventListener("drop", onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed.file, peerId]);

  // Paste into the machine's clipboard - unless it was meant for a field of
  // ours, in which case it would go to the machine instead of into the box it
  // was pasted in.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (!meantForRemote(e.target)) return;
      const text = e.clipboardData?.getData("text/plain");
      if (text) sessionRef.current?.sendClipboardText(text);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  function switchDisplay(i: number) {
    // Set it first: leaving the wall restores whatever display is current, so
    // it has to be the one just chosen.
    setDisplay(i);
    displayRef.current = i;
    if (allScreens) {
      showEveryScreen(false);
      return;
    }
    // Through the shared set, so a screen open in its own window keeps
    // arriving rather than being dropped by a plain switch.
    syncCapture();
  }

  function applyQuality(q: number) {
    setQuality(q);
    sessionRef.current?.updateOptions({ imageQuality: q });
  }

  // --- every screen at once ----------------------------------------------

  /**
   * Asks the machine to capture every display, or to go back to one.
   *
   * The peer encodes each display separately, so this costs real bandwidth and
   * CPU on the machine; it stays off until asked for.
   */
  function showEveryScreen(on: boolean) {
    const count = peerInfoRef.current?.displays.length ?? 0;
    if (on && count < 2) return;
    setAllScreens(on);
    if (on) {
      sessionRef.current?.captureDisplays(
        Array.from({ length: count }, (_, i) => i),
      );
    } else {
      for (const sink of wallRef.current.values()) sink.close();
      wallRef.current.clear();
      wallCanvasRef.current.clear();
      displayRef.current = Math.min(displayRef.current, Math.max(0, count - 1));
    }
    // One place decides what this connection is receiving, so the screens in
    // their own windows are not forgotten on the way in or out of the wall.
    allScreensRef.current = on;
    syncCapture();
  }

  /**
   * The screens this connection should be receiving.
   *
   * The one on the stage, every one when the all-screens view is up, and any
   * being drawn into their own windows. Asking for a set rather than a single
   * screen is what lets a popped-out screen ride on this connection instead
   * of opening another.
   */
  const wantedDisplays = useCallback((): number[] => {
    const count = peerInfoRef.current?.displays.length ?? 0;
    const want = new Set<number>();
    if (allScreensRef.current) for (let i = 0; i < count; i++) want.add(i);
    else want.add(displayRef.current);
    for (const i of mirrorsRef.current.keys()) want.add(i);
    return [...want].filter((i) => i >= 0 && (count === 0 || i < count)).sort((a, b) => a - b);
  }, []);

  /** Tells the machine which screens to send, after the set has changed. */
  const syncCapture = useCallback(() => {
    const want = wantedDisplays();
    if (!want.length) return;
    if (want.length === 1) sessionRef.current?.switchDisplay(want[0]!);
    else sessionRef.current?.captureDisplays(want);
    sessionRef.current?.refreshVideo();
  }, [wantedDisplays]);

  /** What a popped-out window needs to label itself. */
  const tellMirror = useCallback((display: number, win: Window) => {
    const d = peerInfoRef.current?.displays[display];
    try {
      win.postMessage({
        type: "rd-mirror-info",
        display,
        hostname: peerInfoRef.current?.hostname ?? "",
        width: d?.width ?? 0,
        height: d?.height ?? 0,
        state: stateRef.current,
        inputEnabled: inputEnabledRef.current,
      }, window.location.origin);
    } catch {
      /* the window has gone; the close handler tidies up */
    }
  }, []);

  /** Stops drawing a screen into a window that has gone away. */
  const dropMirror = useCallback((display: number) => {
    const entry = mirrorsRef.current.get(display);
    if (!entry) return;
    entry.sink.close();
    mirrorsRef.current.delete(display);
    setPoppedOut((list) => list.filter((i) => i !== display));
    syncCapture();
  }, [syncCapture]);

  /** Attaches a decoder to one screen's canvas in the wall view. */
  const bindWallCanvas = useCallback((index: number, canvas: HTMLCanvasElement | null) => {
    if (!canvas) {
      wallRef.current.get(index)?.close();
      wallRef.current.delete(index);
      wallCanvasRef.current.delete(index);
      setWallEpoch((n) => n + 1);
      return;
    }
    if (wallCanvasRef.current.get(index) === canvas) return;
    wallRef.current.get(index)?.close();
    wallCanvasRef.current.set(index, canvas);
    wallRef.current.set(index, new VideoSink(canvas, {
      onNeedRefresh: () => sessionRef.current?.refreshVideo(),
    }));
    setWallEpoch((n) => n + 1);
  }, []);

  const wallRefFor = useCallback((index: number) => {
    let cb = wallRefs.current.get(index);
    if (!cb) {
      cb = (el: HTMLCanvasElement | null) => bindWallCanvas(index, el);
      wallRefs.current.set(index, cb);
    }
    return cb;
  }, [bindWallCanvas]);

  /**
   * Serves the windows showing a screen of their own.
   *
   * They send a canvas once and input thereafter; this session answers with
   * frames drawn straight into that canvas. Only messages from this origin
   * are believed, and only for windows this session opened.
   */
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!isMirrorMessage(e)) return;
      const msg = e.data;

      if (msg.type === "rd-mirror-ready") {
        if (msg.peerId !== peerId) return;
        const win = e.source as Window | null;
        if (!win) return;
        mirrorsRef.current.get(msg.display)?.sink.close();
        mirrorsRef.current.set(msg.display, {
          win,
          sink: new VideoSink(msg.canvas, {
            onNeedRefresh: () => sessionRef.current?.refreshVideo(),
          }),
        });
        setPoppedOut((list) => (list.includes(msg.display) ? list : [...list, msg.display]));
        // Ask the machine for this screen as well as whatever is on the stage.
        syncCapture();
        tellMirror(msg.display, win);
        return;
      }

      if (msg.type === "rd-mirror-closed") {
        dropMirror(msg.display);
        return;
      }

      if (msg.type === "rd-mirror-input") {
        if (!inputEnabledRef.current) return;
        forwardMirrorInput(msg.display, msg.event);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peerId, syncCapture, tellMirror, dropMirror]);

  /** Keeps those windows' titles honest as things change here. */
  useEffect(() => {
    for (const [display, { win }] of mirrorsRef.current) tellMirror(display, win);
  }, [state, inputEnabled, peerInfo, tellMirror]);

  /** Closes the lent canvases when this session ends. */
  useEffect(() => () => {
    for (const [display, { sink, win }] of mirrorsRef.current) {
      sink.close();
      try {
        win.postMessage({ type: "rd-mirror-closed", display }, window.location.origin);
      } catch { /* already gone */ }
    }
    mirrorsRef.current.clear();
  }, []);

  /**
   * Turns a click in another window into one on the machine's desktop.
   *
   * That window reports where the pointer was as a fraction of its picture,
   * because it is the only one that knows its own size. Here that becomes a
   * point on the whole desktop, using the place the machine said this screen
   * sits - the same arithmetic each panel of the all-screens view uses.
   */
  function forwardMirrorInput(display: number, event: MirrorInput["event"]) {
    const session = sessionRef.current;
    if (!session) return;
    const v = viewportFor(display);
    const pointAt = (nx: number, ny: number) => ({
      x: Math.round(v.remoteX + nx * v.remoteWidth),
      y: Math.round(v.remoteY + ny * v.remoteHeight),
    });

    switch (event.kind) {
      case "move":
        session.sendMouse(mouseMoveEvent(pointAt(event.nx, event.ny)));
        break;
      case "down":
      case "up": {
        const ev = mouseButtonEventFor(
          event.button, event.kind === "down", pointAt(event.nx, event.ny));
        if (ev) session.sendMouse(ev);
        break;
      }
      case "wheel": {
        const at = pointAt(event.nx, event.ny);
        // The same reading of a trackpad as the main stage gives: pixel
        // deltas pan, ctrl pinches, anything else is a wheel in notches.
        if (event.pixels && !event.ctrl) {
          const pan = mirrorPanRef.current;
          if (!pan.has(display)) session.sendTouchPan("start", at.x, at.y);
          session.sendTouchPan("update", -event.dx, -event.dy);
          clearTimeout(pan.get(display));
          pan.set(display, setTimeout(() => {
            pan.delete(display);
            sessionRef.current?.sendTouchPan("end", at.x, at.y);
          }, 120));
          break;
        }
        if (event.ctrl && event.pixels) {
          session.sendTouchScale(-event.dy / 100);
          break;
        }
        session.sendMouse(wheelEventFor(event.dx, event.dy, event.pixels));
        break;
      }
      case "key": {
        const ev = keyEventFor(
          { code: event.code, key: event.key },
          event.down,
          peerPlatformRef.current,
        );
        if (ev) session.sendKey(ev);
        break;
      }
    }
  }

  /**
   * Opens one screen in its own browser window.
   *
   * A second window is a second RemoteSession to the same machine, which is
   * what the desktop client does for a multi-window layout: the peer treats it
   * as another viewer and encodes that display for it.
   */
  function popOutScreen(index: number) {
    const url = new URL(window.location.href);
    /*
     * A window that draws from this connection where the browser allows it,
     * and a session of its own where it does not. The second costs another
     * login and shows the machine a second viewer, which is what this used
     * to do for everyone.
     */
    url.hash = canMirror()
      ? mirrorRoute(peerId, index)
      : `#/session/${encodeURIComponent(peerId)}?display=${index}&window=1`;
    const opened = window.open(
      url.toString(),
      `rd-${peerId}-${index}`,
      "width=1280,height=800,menubar=no,toolbar=no",
    );
    if (!opened) {
      setError("The browser blocked the new window. Allow pop-ups for this site.");
      return;
    }
    setPoppedOut((list) => (list.includes(index) ? list : [...list, index]));
  }

  // --- capture -----------------------------------------------------------

  function takeScreenshot() {
    setNotice("");
    setError("");
    // Ask the machine for a still of the whole display: what it captures is
    // sharper than re-encoding the frame this browser happens to be showing.
    // While every screen is shown, it is the one being pointed at.
    sessionRef.current?.requestScreenshot(allScreens ? activeWall : displayRef.current);
  }

  /**
   * What a recording should capture: the screen on show, or all of them.
   *
   * `captureStream` reads one canvas, and the all-screens view has one per
   * screen, so the panels are drawn into a single canvas laid out the way they
   * appear. Recording the hidden single-screen canvas instead would have
   * produced a black file.
   */
  function recordingSource(): { canvas: HTMLCanvasElement; stop: () => void } | null {
    if (!allScreens) {
      const canvas = canvasRef.current;
      return canvas ? { canvas, stop: () => {} } : null;
    }
    const wall = wallElRef.current;
    const panels = [...wallCanvasRef.current.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([, el]) => el);
    if (!wall || !panels.length) return null;

    const area = wall.getBoundingClientRect();
    // Cap the output so a wall of large screens does not produce a file nobody
    // can play; the layout is kept, only scaled.
    const scale = Math.min(1, 1920 / Math.max(1, area.width));
    const out = document.createElement("canvas");
    out.width = Math.max(2, Math.round(area.width * scale));
    out.height = Math.max(2, Math.round(area.height * scale));
    const ctx = out.getContext("2d");
    if (!ctx) return null;

    let frame = 0;
    const draw = () => {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, out.width, out.height);
      const now = wall.getBoundingClientRect();
      for (const panel of panels) {
        if (!panel.width || !panel.height || !panel.isConnected) continue;
        const r = panel.getBoundingClientRect();
        ctx.drawImage(
          panel,
          (r.x - now.x) * scale, (r.y - now.y) * scale,
          r.width * scale, r.height * scale,
        );
      }
      frame = requestAnimationFrame(draw);
    };
    draw();
    return { canvas: out, stop: () => cancelAnimationFrame(frame) };
  }

  /**
   * Records what is on screen to a file in this browser.
   *
   * The canvas already holds the decoded stream, so recording it needs nothing
   * from the machine - and unlike a server-side recording it cannot be started
   * without the operator seeing it happen.
   */
  function toggleRecording() {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      setError("This browser cannot record video.");
      return;
    }
    const source = recordingSource();
    if (!source) {
      setError("There is nothing on screen to record yet.");
      return;
    }
    const type = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"]
      .find((t) => MediaRecorder.isTypeSupported(t));
    if (!type) {
      source.stop();
      setError("This browser cannot record video.");
      return;
    }
    recordStopRef.current = source.stop;
    try {
      const recorder = new MediaRecorder(source.canvas.captureStream(30), {
        mimeType: type,
        videoBitsPerSecond: 4_000_000,
      });
      recordedRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size) recordedRef.current.push(e.data);
      };
      recorder.onstop = () => {
        setRecording(false);
        recorderRef.current = null;
        recordStopRef.current?.();
        recordStopRef.current = null;
        const blob = new Blob(recordedRef.current, { type });
        recordedRef.current = [];
        if (!blob.size) {
          // Nothing was painted while it ran, so there is nothing to save -
          // say so rather than appearing to have saved a file.
          setError("Nothing was recorded: the machine sent no picture.");
          return;
        }
        downloadBlob(blob, `${peerInfoRef.current?.hostname || peerId}-${stamp()}.webm`);
        setNotice("Recording saved.");
      };
      recorder.onerror = () => setError("The recording stopped unexpectedly.");
      // A timeslice means a crash or a closed tab still leaves usable data.
      recorder.start(2000);
      recorderRef.current = recorder;
      setRecording(true);
      setNotice("");
    } catch (e) {
      source.stop();
      recordStopRef.current = null;
      setError((e as Error).message);
    }
  }

  // --- the machine's own keyboard and screen ------------------------------

  /*
   * These two flip straight away and are corrected if the machine says
   * otherwise. Waiting for the machine instead would be more truthful but
   * worse: not every peer answers - a real Windows machine was seen locking
   * its input without a word about it - and a switch that never moves is one
   * nobody can turn off again.
   */
  function toggleBlockInput() {
    setError("");
    setNotice("");
    const next = !blockedInput;
    setBlockedInput(next);
    sessionRef.current?.setBlockInput(next);
  }

  /**
   * Blanks the machine's own screen.
   *
   * There can be several ways to do it and they behave differently - one hides
   * the session from the local screen, another switches the machine to a
   * virtual display - so the machine's own list is offered rather than a
   * single switch.
   */
  function togglePrivacy(implKey = "") {
    setError("");
    setNotice("");
    const next = !privacy;
    setPrivacy(next);
    sessionRef.current?.setPrivacyMode(next, implKey);
  }

  /**
   * Answers a machine waiting to be told which Windows session to work in.
   *
   * Choosing one it is not already in makes it move there, which drops the
   * connection - so that case reconnects, arriving in the chosen session.
   */
  function chooseWindowsSession(sid: number) {
    const movingSession = sid !== currentSid;
    rememberSession(peerId, sid);
    setSessionChoice(null);
    setError("");
    sessionRef.current?.selectWindowsSession(sid);
    if (!movingSession) return;

    setNotice("Moving the machine to that session\u2026");
    sessionRef.current?.close("changing Windows session");
    // The machine needs a moment to bring the session up before it will
    // answer again.
    setTimeout(() => void start(password || undefined), 2500);
  }

  /**
   * Sets the machine's screen resolution.
   *
   * Worth having on a machine with no real graphics adapter, where the basic
   * driver offers one unhelpful size - and the resolution follows the screen,
   * so it is offered per screen rather than globally.
   */
  function setResolution(width: number, height: number) {
    setError("");
    setNotice(`Asking the machine for ${width}\u00d7${height}\u2026`);
    sessionRef.current?.setDisplayResolution(displayRef.current, width, height);
  }

  function toggleFollowDisplay() {
    const next = !followDisplay;
    setFollowDisplay(next);
    sessionRef.current?.followCurrentDisplay(next);
    setNotice(next
      ? "Following whichever screen is in use at the machine."
      : "Staying on this screen.");
  }

  /**
   * Sends dropped files to the machine's desktop.
   *
   * On its own connection, like the file browser: the screen session is not a
   * file-transfer session and cannot carry them.
   */
  async function sendFilesToMachine(files: File[]) {
    setError("");
    const names = files.map((f) => f.name).join(", ");
    setSendingFiles(names);

    const { RemoteSession: Session } = await import("rustdesk-protocol/session.js");
    const { FileTransfer } = await import("rustdesk-protocol/files.js");
    let transfer: InstanceType<typeof FileTransfer> | null = null;
    let session: InstanceType<typeof Session> | null = null;
    try {
      const cfg = await api.get<ServerConfig>("/api/console/generator/config");
      const hash = password ? null : await savedHash(peerId);
      // The machine's own Desktop, which is where a person expects a dropped
      // file to land.
      const target = peerPlatformRef.current === "windows"
        ? "%USERPROFILE%\\Desktop"
        : "~/Desktop";

      await new Promise<void>((resolve, reject) => {
        session = new Session({
          origin: window.location.origin,
          token: getToken(),
          peerId,
          serverKey: cfg.key,
          password: password || undefined,
          passwordHash: hash ?? undefined,
          connType: "file_transfer",
          fileTransferDir: target,
          windowsSessionId: rememberedSession(peerId),
          myName: me.display_name || me.name,
          myId: "web",
        }, {
          onPeerInfo: () => resolve(),
          onError: (m) => reject(new Error(m)),
          onClose: (r) => reject(new Error(r || "The machine closed the connection")),
          onPasswordRequired: () => reject(new Error("This machine needs a password")),
          onMessage: (m) => transfer?.handle(m),
        });
        transfer = new FileTransfer(session);
        session.connect().catch(reject);
      });

      for (const file of files) {
        setSendingFiles(`${file.name} (${files.indexOf(file) + 1} of ${files.length})`);
        await transfer!.upload(target, file);
      }
      setNotice(files.length === 1
        ? `${files[0]!.name} was sent to the machine's desktop.`
        : `${files.length} files were sent to the machine's desktop.`);
    } catch (e) {
      setError(`Could not send ${names}: ${(e as Error).message}`);
    } finally {
      setSendingFiles("");
      try { (session as { close?: (r: string) => void } | null)?.close?.("upload finished"); }
      catch { /* already gone */ }
    }
  }

  function toggleVirtualDisplay(on: boolean) {
    setError("");
    sessionRef.current?.setVirtualDisplay(0, on);
    setNotice(on
      ? "Asked the machine to add a virtual display. It may take a few seconds to appear."
      : "Asked the machine to remove its virtual display.");
  }

  // --- voice call ---------------------------------------------------------

  const stopVoice = useCallback(() => {
    if (ringTimerRef.current) clearTimeout(ringTimerRef.current);
    ringTimerRef.current = null;
    voiceRef.current?.stop();
    voiceRef.current = null;
    speakerRef.current?.close();
    speakerRef.current = null;
  }, []);
  stopVoiceRef.current = stopVoice;

  async function startCall() {
    setError("");
    if (!(await isVoiceSupported())) {
      setError("This browser cannot make a voice call. Chrome, Edge or Safari 16.4+ can.");
      return;
    }
    const player = new AudioPlayer((m) => setError(m));
    // A browser keeps audio suspended until a gesture; this runs inside one.
    await player.resume();
    speakerRef.current = player;

    const capture = new AudioCapture();
    try {
      await capture.start(
        (packet) => sessionRef.current?.sendAudio(packet),
        (m) => setError(m),
      );
    } catch (e) {
      stopVoice();
      setError((e as Error).message);
      return;
    }
    voiceRef.current = capture;
    setVoice("asking");
    sessionRef.current?.setVoiceCall(true);
    // The machine never answers on its own: RustDesk puts the request in front
    // of whoever is sitting there and waits. If nobody is, nothing ever comes
    // back - so give up rather than ring forever holding the microphone.
    ringTimerRef.current = setTimeout(() => {
      ringTimerRef.current = null;
      endCall();
      setError(
        "Nobody answered. A call has to be accepted in the small RustDesk window on " +
        "the machine itself - it sits on the desktop you are viewing, so you can " +
        "click Accept there and call again.",
      );
    }, 45_000);
  }

  function endCall() {
    sessionRef.current?.setVoiceCall(false);
    stopVoice();
    setVoice("off");
  }

  // --- notes --------------------------------------------------------------

  async function saveNote() {
    setNoteState("saving");
    try {
      const page = await api.get<{ data: Array<{ guid: string; id: string }> }>(
        `/api/console/devices?pageSize=500`,
      );
      const device = page.data.find((d) => d.id === peerId);
      if (!device) throw new Error("This machine is not in the console yet.");
      await api.patch(`/api/console/devices/${device.guid}`, { note });
      setNoteState("saved");
      setTimeout(() => setNoteState("idle"), 2000);
    } catch (e) {
      setNoteState("idle");
      setError((e as Error).message);
    }
  }

  const connected = state === "connected";
  const displays = peerInfo?.displays ?? [];

  const screenItems: MenuItem[] = [
    ...displays.map((d, i) => ({
      label: `Screen ${i + 1}`,
      hint: `${d.width}\u00d7${d.height}`,
      checked: !allScreens && display === i,
      onSelect: () => switchDisplay(i),
    })),
    {
      label: "Every screen at once",
      hint: displays.length < 2 ? "needs 2 screens" : "",
      checked: allScreens,
      disabled: displays.length < 2,
      separated: true,
      onSelect: () => showEveryScreen(!allScreens),
    },
    ...displays.map((_, i) => ({
      label: `Screen ${i + 1} in a new window`,
      checked: poppedOut.includes(i),
      separated: i === 0,
      onSelect: () => popOutScreen(i),
    })),
    // A machine with no real graphics adapter - a VM, a headless server -
    // often has one screen at a very low refresh rate, and no client can show
    // more frames than the machine produces. A virtual display fixes both.
    {
      label: "Follow the screen in use at the machine",
      hint: displays.length < 2 ? "needs 2 screens" : "",
      checked: followDisplay,
      disabled: displays.length < 2,
      separated: true,
      onSelect: toggleFollowDisplay,
    },
    // Only a handful, nearest the current size: a machine can offer dozens and
    // a menu of them is unusable.
    ...(resolutions.length
      ? offeredResolutions(resolutions, displays[display]).map((r) => ({
          label: `Set this screen to ${r.width}\u00d7${r.height}`,
          checked: displays[display]?.width === r.width
            && displays[display]?.height === r.height,
          separated: r === offeredResolutions(resolutions, displays[display])[0],
          onSelect: () => setResolution(r.width, r.height),
        }))
      : []),
    ...(windowsSessions.length > 1
      ? [
          ...windowsSessions.map((w) => ({
            label: `Session: ${w.name || `#${w.sid}`}`,
            hint: w.sid === currentSid ? "in use" : "",
            checked: w.sid === currentSid,
            separated: w.sid === windowsSessions[0]!.sid,
            onSelect: () => chooseWindowsSession(w.sid),
          })),
        ]
      : []),
    ...(caps.virtualDisplay
      ? [
          {
            label: "Add a virtual display",
            hint: "smoother",
            separated: true,
            onSelect: () => toggleVirtualDisplay(true),
          },
          {
            label: "Remove the virtual display",
            disabled: displays.length < 2,
            onSelect: () => toggleVirtualDisplay(false),
          },
        ]
      : []),
  ];

  const actionItems: MenuItem[] = [
    { label: "Take a screenshot", onSelect: takeScreenshot },
    ...(allowed.file === false
      ? [{ label: "File transfer", hint: "turned off at the machine", disabled: true, onSelect: () => {} }]
      : []),
    {
      label: recording
        ? "Stop recording"
        : allScreens ? "Record every screen" : "Record this screen",
      hint: recording ? "saves a file" : "",
      onSelect: toggleRecording,
    },
    { label: "Type text\u2026", separated: true, onSelect: () => setDialog("type") },
    { label: "Send Ctrl+Alt+Del", onSelect: () => sessionRef.current?.sendKey(ctrlAltDelEvent()) },
    { label: "Lock the machine", onSelect: () => sessionRef.current?.sendKey(lockScreenEvent()) },
    {
      label: "Lock out the keyboard and mouse there",
      hint: allowed.blockInput === false ? "turned off at the machine" : "",
      disabled: allowed.blockInput === false,
      checked: blockedInput,
      separated: true,
      onSelect: toggleBlockInput,
    },
    // The machine lists how it can do this; there is often more than one way
    // and they behave differently.
    ...(privacy
      ? [{ label: "Stop blanking the machine's screen", checked: true, onSelect: () => togglePrivacy() }]
      : caps.privacyModeImpls.length
        ? caps.privacyModeImpls.map((impl) => ({
            label: impl.label,
            onSelect: () => togglePrivacy(impl.key),
          }))
        : [{
            label: "Blank the machine's own screen",
            hint: caps.canBlankScreen ? "" : "not available",
            disabled: !caps.canBlankScreen,
            onSelect: () => togglePrivacy(),
          }]),
    {
      label: elevated ? "Running as administrator" : "Ask for administrator rights",
      disabled: elevating || elevated,
      separated: true,
      onSelect: () => {
        setError("");
        setNotice("");
        setElevating(true);
        sessionRef.current?.requestElevation();
      },
    },
    { label: "Sign in with Windows credentials\u2026", onSelect: () => setDialog("oslogin") },
    { label: "Ask for a fresh image", separated: true, onSelect: () => sessionRef.current?.refreshVideo() },
    {
      label: "Restart the machine",
      hint: allowed.restart === false ? "turned off at the machine" : "",
      disabled: allowed.restart === false,
      danger: true,
      onSelect: () => setDialog("restart"),
    },
    {
      label: "Stop RustDesk on the machine",
      hint: "nobody can connect after this",
      danger: true,
      onSelect: () => setDialog("stopservice"),
    },
    ...(usingSaved
      ? [{
          label: "Forget this machine's password",
          danger: true,
          onSelect: () => {
            void forgetHash(peerId).catch(() => {});
            setUsingSaved(false);
          },
        }]
      : []),
  ];

  return (
    <div className="session">
      <div className="session__bar">
        <button className="btn btn--ghost btn--sm" onClick={() => { sessionRef.current?.close(); onExit(); }}>
          {secondaryWindow ? "Close" : "Leave"}
        </button>

        <span className="row" style={{ gap: "0.5rem" }}>
          <span className={`dot${connected ? " dot--live" : state === "closed" ? " dot--deny" : " dot--warn"}`} />
          <strong style={{ fontSize: 13 }}>
            {peerInfo?.hostname || deviceId(peerId)}
          </strong>
          {secondaryWindow ? (
            <span className="small faint">Screen {(initialDisplay ?? 0) + 1}</span>
          ) : null}
          {!connected ? <span className="small faint">{STATE_TEXT[state]}</span> : null}
        </span>

        {recording ? (
          <span className="session__flag session__flag--rec" title="Recording to a file in this browser">
            <span className="session__flag-dot" /> Recording
          </span>
        ) : null}
        {blockedInput ? (
          <span className="session__flag" title="Nobody at the machine can use its keyboard or mouse">
            <span className="session__flag-dot" /> Input locked
          </span>
        ) : null}
        {allowed.keyboard === false ? (
          <span className="session__flag" title="The machine has turned off keyboard and mouse">
            <span className="session__flag-dot" /> Control off at the machine
          </span>
        ) : null}
        {privacy ? (
          <span className="session__flag" title="The machine's own screen is blank">
            <span className="session__flag-dot" /> Screen blanked
          </span>
        ) : null}
        {voice !== "off" ? (
          <span
            className="session__flag"
            title={voice === "on"
              ? "A voice call is open"
              : "Waiting for someone at the machine to accept"}
          >
            <span className="session__flag-dot" /> {voice === "on" ? "On a call" : "Ringing\u2026"}
          </span>
        ) : null}

        <div className="spacer" />

        {connected ? (
          <>
            {/*
              * One button per screen, laid out the way the screens are.
              *
              * Sorted by the x the machine reports, so the leftmost button is
              * the leftmost monitor and clicking it needs no thought. The
              * label stays the machine's own numbering, which is what every
              * other mention of a screen in this page uses.
              */}
            {!secondaryWindow && displays.length > 1 ? (
              <div className="screens" role="group" aria-label="Which screen to see">
                {displays
                  .map((d, i) => ({ d, i }))
                  .sort((a, b) => a.d.x - b.d.x)
                  .map(({ d, i }) => (
                    <button
                      key={i}
                      className={`screens__btn${!allScreens && display === i ? " screens__btn--on" : ""}`}
                      onClick={() => switchDisplay(i)}
                      aria-pressed={!allScreens && display === i}
                      title={`Screen ${i + 1} \u2014 ${d.width}\u00d7${d.height}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                <button
                  className={`screens__btn screens__btn--all${allScreens ? " screens__btn--on" : ""}`}
                  onClick={() => showEveryScreen(!allScreens)}
                  aria-pressed={allScreens}
                  title="Every screen at once"
                >
                  All
                </button>
              </div>
            ) : null}

            {!secondaryWindow ? (
              <Menu
                label={displays.length > 1 ? "Screens" : `Screen ${display + 1}`}
                items={screenItems}
                title="Choose which screen to see"
              />
            ) : null}

            <select
              value={quality}
              onChange={(e) => applyQuality(Number(e.target.value))}
              style={{ width: "auto" }}
              aria-label="Image quality"
            >
              <option value={ImageQuality.Low}>Fastest</option>
              <option value={ImageQuality.Balanced}>Balanced</option>
              <option value={ImageQuality.Best}>Sharpest</option>
            </select>

            <button
              className="btn btn--sm"
              onClick={() => setInputEnabled((v) => !v)}
              title={inputEnabled ? "Stop sending keyboard and mouse" : "Send keyboard and mouse"}
            >
              {inputEnabled ? "Viewing + control" : "View only"}
            </button>

            <Menu label="Actions" items={actionItems} align="right" />

            {!secondaryWindow ? (
              <>
                <button
                  className="btn btn--sm"
                  onClick={() => setSide((v) => (v === "chat" ? "none" : "chat"))}
                  title="Type to whoever is at the machine"
                >
                  Chat{unreadChat ? ` (${unreadChat})` : ""}
                </button>
                <button
                  className="btn btn--sm"
                  onClick={() => (voice === "off" ? void startCall() : endCall())}
                  title="Talk to whoever is at the machine"
                >
                  {voice === "off" ? "Call" : "End call"}
                </button>
                <Menu
                  label="More"
                  align="right"
                  items={[
                    { label: "Terminal", onSelect: () => setShowTerminal(true) },
                    {
                      label: "Files",
                      hint: allowed.file === false ? "turned off at the machine" : "",
                      disabled: allowed.file === false,
                      onSelect: () => setShowFiles(true),
                    },
                    {
                      label: "Camera",
                      hint: caps.viewCamera ? "" : "not available",
                      disabled: !caps.viewCamera,
                      onSelect: () => setShowCamera(true),
                    },
                    {
                      label: "Note about this machine",
                      separated: true,
                      checked: side === "note",
                      onSelect: () => setSide((v) => (v === "note" ? "none" : "note")),
                    },
                  ]}
                />
              </>
            ) : null}

            <span className="session__stat">
              {allScreens
                ? `${displays.length} screens`
                : `${stats.width}\u00d7${stats.height}`} &nbsp; {stats.fps} fps &nbsp; {stats.codec.toUpperCase()}
              {sessionRef.current?.secured ? " \u00b7 encrypted" : " \u00b7 unencrypted"}
            </span>
          </>
        ) : null}
      </div>

      {uacBlocking && !elevated ? (
        <div className="notice notice--warn" style={{ borderRadius: 0, borderWidth: "0 0 1px" }}>
          A window on the machine is running with administrator rights, so it is
          ignoring keyboard and mouse input.{" "}
          <button
            className="btn btn--sm"
            disabled={elevating}
            onClick={() => { setElevating(true); sessionRef.current?.requestElevation(); }}
          >
            Elevate
          </button>{" "}
          <span className="small faint">
            Someone at the machine has to accept the prompt.
          </span>
        </div>
      ) : null}
      {sendingFiles ? (
        <div className="notice" style={{ borderRadius: 0, borderWidth: "0 0 1px" }}>
          Sending to the machine&rsquo;s desktop: <strong>{sendingFiles}</strong>
        </div>
      ) : null}

      {clipboardFromPeer ? (
        <div className="notice" style={{ borderRadius: 0, borderWidth: "0 0 1px" }}>
          The machine copied something, and this browser would not put it on your
          clipboard for you.{" "}
          <button
            className="btn btn--sm"
            onClick={() => {
              void navigator.clipboard?.writeText(clipboardFromPeer)
                .then(() => setClipboardFromPeer(""))
                .catch(() => {});
            }}
          >
            Copy it
          </button>{" "}
          <button className="btn btn--ghost btn--sm" onClick={() => setClipboardFromPeer("")}>
            Dismiss
          </button>
          <div className="mono small faint truncate" style={{ marginTop: "0.375rem" }}>
            {clipboardFromPeer.slice(0, 160)}
          </div>
        </div>
      ) : null}

      {sessionChoice ? (
        <div className="notice notice--warn" style={{ borderRadius: 0, borderWidth: "0 0 1px" }}>
          <strong>This machine has more than one Windows session.</strong>{" "}
          It is waiting to be told which one to work in &mdash; nothing starts
          until you choose.{" "}
          <span className="row" style={{ gap: "0.375rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
            {sessionChoice.sessions.map((w) => (
              <button
                key={w.sid}
                className={w.sid === sessionChoice.currentSid ? "btn btn--primary btn--sm" : "btn btn--sm"}
                onClick={() => chooseWindowsSession(w.sid)}
              >
                {w.name || `Session ${w.sid}`}
                {w.sid === sessionChoice.currentSid ? " (in use)" : ""}
              </button>
            ))}
          </span>{" "}
          <span className="small faint">
            Choosing one the machine is not already in moves its console there.
          </span>
        </div>
      ) : null}

      {voice === "asking" ? (
        <div className="notice notice--warn" style={{ borderRadius: 0, borderWidth: "0 0 1px" }}>
          <strong>Ringing.</strong> RustDesk cannot answer on its own. Accept it in the
          small <strong>RustDesk</strong> window on the machine &mdash; it is on the
          desktop you are looking at, so you can click it from here.{" "}
          <button className="btn btn--sm" onClick={endCall}>Cancel</button>
        </div>
      ) : null}
      {connected && error ? (
        <div
          className="notice notice--error"
          style={{ borderRadius: 0, borderWidth: "0 0 1px" }}
          role="alert"
        >
          {error}{" "}
          <button className="btn btn--sm" onClick={() => setError("")}>Dismiss</button>
        </div>
      ) : null}
      {notice ? (
        <div className="notice notice--ok" style={{ borderRadius: 0, borderWidth: "0 0 1px" }}>
          {notice}{" "}
          <button className="btn btn--ghost btn--sm" onClick={() => setNotice("")}>Dismiss</button>
        </div>
      ) : null}

      <div className="session__body">
      <div className="session__stage" ref={stageRef} data-dropping={dropping || undefined}>
        {/*
          The single-screen canvas stays mounted while the wall is up: it holds
          the decoder, the cursor and every input handler, and tearing it down
          to switch view would drop all three.
        */}
        <canvas
          ref={canvasRef}
          className="session__canvas session__canvas--fit"
          tabIndex={0}
          hidden={allScreens}
        />

        {allScreens ? (
          <div
            ref={wallElRef}
            className="wall"
            style={{
              // A near-square arrangement: two screens side by side, four in a
              // block. `auto-fit` cannot know how many there are and left them
              // crammed into a fraction of the width.
              gridTemplateColumns:
                `repeat(${Math.ceil(Math.sqrt(displays.length))}, minmax(0, 1fr))`,
            }}
          >
            {displays.map((d, i) => (
              <div className="wall__cell" key={i} data-active={activeWall === i}>
                <canvas
                  ref={wallRefFor(i)}
                  onDoubleClick={() => switchDisplay(i)}
                  title="Click to use this screen, double-click to fill the window with it"
                />
                <span className="wall__tag">
                  {i + 1} &nbsp; {d.width}&times;{d.height}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {!connected ? (
          <div className="session__status">
            <div style={{ maxWidth: 460, pointerEvents: "auto" }}>
              {needPassword || waitingForAccept ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!password) return;
                    if (remember) pendingSaveRef.current = password;
                    void sessionRef.current?.retryWithPassword(password);
                    setNeedPassword(false);
                    setWaitingForAccept(false);
                  }}
                >
                  <h2 style={{ marginBottom: "0.5rem" }}>
                    {wrongPassword
                      ? "That password was not accepted"
                      : waitingForAccept
                        ? "Waiting for someone to accept"
                        : "This machine needs a password"}
                  </h2>
                  {waitingForAccept && !wrongPassword ? (
                    <p className="faint" style={{ marginBottom: "0.75rem" }}>
                      Someone at the machine can accept this connection. If you know its
                      password, enter it to connect without waiting.
                    </p>
                  ) : null}
                  <div className="field">
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Machine password"
                      autoFocus
                    />
                  </div>
                  <label className="check" style={{ justifyContent: "center" }}>
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                    />
                    Remember this machine
                  </label>
                  <button className="btn btn--primary" disabled={!password}>
                    Connect
                  </button>
                </form>
              ) : error ? (
                <>
                  <h2 style={{ marginBottom: "0.5rem" }}>Could not connect</h2>
                  <div style={{ marginBottom: "1rem" }}><Notice>{error}</Notice></div>
                  <div className="row" style={{ justifyContent: "center" }}>
                    <button className="btn btn--primary" onClick={() => void start(password || undefined)}>
                      Try again
                    </button>
                    <button className="btn" onClick={onExit}>Back to machines</button>
                  </div>
                </>
              ) : state === "closed" ? (
                <>
                  <h2 style={{ marginBottom: "0.5rem" }}>Session ended</h2>
                  <p className="faint">{detail || "The machine closed the connection."}</p>
                  <div className="row" style={{ justifyContent: "center" }}>
                    <button className="btn btn--primary" onClick={() => void start(password || undefined)}>
                      Reconnect
                    </button>
                    <button className="btn" onClick={onExit}>Back to machines</button>
                  </div>
                </>
              ) : (
                <>
                  <p className="faint">{STATE_TEXT[state]}…</p>
                  {canSendPassword ? (
                    <button
                      className="btn btn--sm"
                      style={{ marginTop: "0.75rem" }}
                      onClick={() => setNeedPassword(true)}
                    >
                      Enter a password instead
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {side !== "none" && !secondaryWindow ? (
        <aside className="session__side">
          <div className="session__side-head">
            {side === "chat" ? "Chat" : "Note"}
            <button
              className="btn btn--ghost btn--sm"
              style={{ marginLeft: "auto" }}
              onClick={() => setSide("none")}
            >
              Close
            </button>
          </div>

          {side === "chat" ? (
            <>
              <div className="session__side-body">
                {chatLog.length ? (
                  <div className="chat">
                    {chatLog.map((m, i) => (
                      <div key={i} className={`chat__msg chat__msg--${m.mine ? "me" : "them"}`}>
                        <span className="chat__who">
                          {m.mine ? "You" : peerInfo?.username || "The machine"}
                        </span>
                        {m.text}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="faint small">
                    Anything you send appears in RustDesk on the machine, so someone
                    there can read it and reply.
                  </p>
                )}
              </div>
              <form
                className="session__side-foot"
                onSubmit={(e) => {
                  e.preventDefault();
                  const text = chatDraft.trim();
                  if (!text) return;
                  sessionRef.current?.sendChat(text);
                  setChatLog((log) => [...log, { mine: true, text, at: Date.now() }]);
                  setChatDraft("");
                }}
              >
                <div className="row">
                  <input
                    className="input"
                    value={chatDraft}
                    placeholder="Write a message"
                    onChange={(e) => setChatDraft(e.target.value)}
                  />
                  <button className="btn btn--primary btn--sm" disabled={!chatDraft.trim()}>
                    Send
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="session__side-body">
                <p className="faint small" style={{ marginBottom: "0.5rem" }}>
                  Saved on the machine&rsquo;s record in the console, where everyone
                  with access to it can read it.
                </p>
                <textarea
                  className="input"
                  rows={14}
                  value={note}
                  placeholder="What this machine is for, who uses it, anything worth knowing next time"
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              <div className="session__side-foot">
                <button
                  className="btn btn--primary btn--sm"
                  disabled={noteState === "saving"}
                  onClick={() => void saveNote()}
                >
                  {noteState === "saving" ? "Saving\u2026" : noteState === "saved" ? "Saved" : "Save note"}
                </button>
              </div>
            </>
          )}
        </aside>
      ) : null}
      </div>

      {dialog === "restart" || dialog === "stopservice" ? (
        <Confirm
          title={dialog === "restart"
            ? `Restart ${peerInfo?.hostname || deviceId(peerId)}?`
            : `Stop RustDesk on ${peerInfo?.hostname || deviceId(peerId)}?`}
          body={dialog === "restart"
            ? "Anyone working on it loses what they have not saved, and this session ends until it comes back."
            : "This session ends and nobody can connect to the machine again until someone starts RustDesk on it in person."}
          confirmLabel={dialog === "restart" ? "Restart it" : "Stop it"}
          onCancel={() => setDialog("none")}
          onConfirm={async () => {
            if (dialog === "restart") {
              sessionRef.current?.restartMachine();
              setNotice("The machine was asked to restart.");
            } else {
              sessionRef.current?.stopRemoteService();
              setNotice("RustDesk on the machine was asked to stop.");
            }
            setDialog("none");
          }}
        />
      ) : null}

      {dialog === "type" ? (
        <TypeTextDialog
          onClose={() => setDialog("none")}
          onSend={(text) => {
            sessionRef.current?.typeText(text);
            setDialog("none");
            setNotice("Text typed at the machine.");
          }}
        />
      ) : null}

      {dialog === "oslogin" ? (
        <OsLoginDialog
          onClose={() => setDialog("none")}
          onSubmit={(username, password2, mode) => {
            setDialog("none");
            if (mode === "elevate") {
              setElevating(true);
              sessionRef.current?.requestElevationWithLogon(username, password2);
            } else {
              // Signing in to the machine's own desktop is part of the login
              // exchange, so it means reconnecting with those credentials.
              osLoginRef.current = { username, password: password2 };
              sessionRef.current?.close("reconnecting with Windows credentials");
              void start(password || undefined);
            }
          }}
        />
      ) : null}

      {showTerminal ? (
        <TerminalView
          peerId={peerId}
          me={me}
          serverKeyProvider={() => api.get<ServerConfig>("/api/console/generator/config")}
          password={password}
          onClose={() => setShowTerminal(false)}
        />
      ) : null}

      {showFiles && sessionRef.current ? (
        <FileBrowser
          peerId={peerId}
          me={me}
          serverKeyProvider={() => api.get<ServerConfig>("/api/console/generator/config")}
          password={password}
          onClose={() => setShowFiles(false)}
        />
      ) : null}

      {showCamera ? (
        <CameraView
          peerId={peerId}
          me={me}
          serverKeyProvider={() => api.get<ServerConfig>("/api/console/generator/config")}
          password={password}
          onClose={() => setShowCamera(false)}
        />
      ) : null}
    </div>
  );
}


/**
 * Types text at the machine one key at a time.
 *
 * A clipboard paste is ignored by some remote fields - UAC prompts, a VM
 * console, an RDP session inside the session - which only accept real key
 * presses. This is also the way to get a password in without it passing
 * through either clipboard.
 */
function TypeTextDialog({
  onClose, onSend,
}: { onClose: () => void; onSend: (text: string) => void }) {
  const [text, setText] = useState("");
  const [hide, setHide] = useState(false);

  return (
    <Drawer
      title="Type text at the machine"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" disabled={!text} onClick={() => onSend(text)}>
            Type it
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="notice">
          Sent as individual key presses, so it works where a paste does not -
          UAC prompts, virtual machine consoles, remote desktops inside this one.
          Click where it should go first.
        </div>
        <label className="field">
          <span>Text</span>
          {hide ? (
            <input
              className="input"
              type="password"
              value={text}
              autoFocus
              onChange={(e) => setText(e.target.value)}
            />
          ) : (
            <textarea
              className="input mono"
              rows={6}
              value={text}
              autoFocus
              spellCheck={false}
              onChange={(e) => setText(e.target.value)}
            />
          )}
        </label>
        <label className="row small" style={{ gap: 6 }}>
          <input type="checkbox" checked={hide} onChange={(e) => setHide(e.target.checked)} />
          Hide what I am typing
        </label>
      </div>
    </Drawer>
  );
}

/**
 * Windows credentials for the machine itself.
 *
 * Two different things, both needing an account on the machine:
 *  - signing in at its lock or sign-in screen, which the peer handles as part
 *    of the login exchange, so it means reconnecting;
 *  - elevating without anyone there to accept a UAC prompt.
 */
function OsLoginDialog({
  onClose, onSubmit,
}: {
  onClose: () => void;
  onSubmit: (username: string, password: string, mode: "signin" | "elevate") => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"signin" | "elevate">("signin");

  return (
    <Drawer
      title="Sign in with Windows credentials"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn btn--primary"
            disabled={!username || !password}
            onClick={() => onSubmit(username, password, mode)}
          >
            {mode === "signin" ? "Sign in" : "Elevate"}
          </button>
        </>
      }
    >
      <div className="stack">
        <label className="field">
          <span>What for</span>
          <select className="input" value={mode} onChange={(e) => setMode(e.target.value as "signin" | "elevate")}>
            <option value="signin">Get past the machine&rsquo;s sign-in or lock screen</option>
            <option value="elevate">Run as administrator, with nobody there to accept</option>
          </select>
        </label>
        <label className="field">
          <span>Username</span>
          <input
            className="input"
            value={username}
            autoFocus
            placeholder="DOMAIN\\user or .\\localuser"
            onChange={(e) => setUsername(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Password</span>
          <input
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <div className="notice">
          These go to the machine for this one action. Nothing is stored here,
          and the connection carrying them is encrypted end to end.
          {mode === "signin" ? " The session reconnects to use them." : ""}
        </div>
      </div>
    </Drawer>
  );
}


/**
 * The resolutions worth offering.
 *
 * A machine can list dozens, most of them useless: a menu of every mode its
 * driver reports is not a menu anyone can use. This keeps the largest few plus
 * whatever it is on now, newest-largest first.
 */
function offeredResolutions(
  all: Array<{ width: number; height: number }>,
  current: { width: number; height: number } | undefined,
): Array<{ width: number; height: number }> {
  const seen = new Set<string>();
  const unique = all.filter((r) => {
    const key = `${r.width}x${r.height}`;
    if (seen.has(key) || r.width < 640 || r.height < 480) return false;
    seen.add(key);
    return true;
  });
  unique.sort((a, b) => b.width * b.height - a.width * a.height);

  const top = unique.slice(0, 6);
  if (current && !top.some((r) => r.width === current.width && r.height === current.height)) {
    const match = unique.find((r) => r.width === current.width && r.height === current.height);
    if (match) top.push(match);
  }
  return top;
}
